import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VideoCallTracksStateService } from 'src/app/services/video-call-tracks-state.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { combineLatest, filter, tap } from 'rxjs';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { BoardRemoteVideoConfigurations } from '../items-canvas/board-items/board-remote-video-configurations';

@UntilDestroy()
@Component({
  selector: 'app-remote-video-item-canvas',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './remote-video-item-canvas.component.html',
  styleUrls: ['./remote-video-item-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteVideoItemCanvasComponent implements AfterViewInit, OnDestroy {
  @Input() state!: BoardRemoteVideoConfigurations;
  @Output() deleteItem = new EventEmitter<void>();
  @Output() stateChanged = new EventEmitter<BoardRemoteVideoConfigurations>();
  @ViewChild('remoteVideo') remoteVideo?: ElementRef;

  private domListener = this.domListenerFactoryService.createInstance();

  constructor(
    private videoCallTracksStateService: VideoCallTracksStateService,
    private providerState: ProviderStateService,
    private domListenerFactoryService: DomListenerFactoryService,
    public spaceRepo: SpaceRepository,
  ) {
    // Cover the case of video's owner reload/close the tab
    this.domListener.add(window, 'beforeunload', () => {
      this.deleteCurrentUserVideo();
    });
  }

  ngAfterViewInit(): void {
    this.setupStreamSubscription();
  }

  private setupStreamSubscription() {
    combineLatest([
      this.providerState.callConnected$,
      this.providerState.localParticipantId$,
      this.videoCallTracksStateService.getPariticpantTrackObservable(
        this.state.participantId,
        this.state.trackType as TrackType,
        this,
      ),
    ])
      .pipe(
        untilDestroyed(this),
        filter(([_, localParticipantId]) => Boolean(localParticipantId)),
        tap(([_, localParticipantId]) => {
          // Force subscribing to the video feed as local user may be on another page that doesn't contain the inserted stream
          // Or was already at minimized view/... etc.
          this.videoCallTracksStateService.updateTrackSubscription(
            this.state.participantId,
            this.state.trackType,
            true,
            localParticipantId === this.state.participantId,
          );
        }),
      )
      .subscribe(([callConnected, _, stream]) => {
        const videoElementRef = this.remoteVideo?.nativeElement as HTMLVideoElement;
        if (stream && callConnected) {
          videoElementRef.srcObject = new MediaStream([stream]);
          videoElementRef.play();
        } else {
          videoElementRef.pause();
          videoElementRef.srcObject = null;
          this.deleteCurrentUserVideo();
        }
      });
  }

  ngOnDestroy() {
    this.domListener.clear();

    // Remove remote video on destroy if the current user owns this feed
    // It's used to cover the case of leaving the space during an active call
    if (this.spaceRepo.activeSpace?._id && this.spaceRepo.activeSpaceSelectedBoardUid) {
      this.deleteCurrentUserVideo();
    }
  }

  private deleteCurrentUserVideo() {
    if (this.providerState.localParticipantIdValue === this.state.participantId) {
      this.deleteItem.emit();
    }
  }
}
